<template>
    <IntegrationDetailDialog
        gradient="mcp-gradient"
        :requires-sub="true"
        :inset-description="false"
        :show-trigger="showTrigger"
        v-model:open="detailDialogOpen"
    >
        <template v-if="$slots.trigger" #trigger>
            <slot name="trigger" />
        </template>

        <template #icon>
            <Bot :size="56" />
        </template>

        <template #title>{{ $t("integrations.mcp.title") }}</template>

        <template #short-description>
            {{ $t("integrations.mcp.description") }}
        </template>
        <template #description>
            {{ $t("integrations.mcp.long_description") }}
        </template>

        <div v-if="mcp" class="px-6">
            <div class="gap-2 grid">
                <Label>{{ $t("integrations.mcp.modal.server_url") }}</Label>
                <div class="gap-2 flex items-center">
                    <Input
                        :model-value="mcp.server_url"
                        readonly
                        class="font-mono text-sm"
                    />
                    <Button
                        variant="outline"
                        type="button"
                        class="gap-1.5 shrink-0"
                        @click="copyServerUrl"
                    >
                        <Check
                            v-if="copied"
                            :size="16"
                            class="text-emerald-600"
                        />
                        <Copy v-else :size="16" />
                        {{
                            copied
                                ? $t("integrations.mcp.modal.copied")
                                : $t("integrations.mcp.modal.copy")
                        }}
                    </Button>
                </div>
            </div>

            <div class="mt-4">
                <p class="text-sm font-medium text-iron-700">
                    {{ $t("integrations.mcp.setup.title") }}
                </p>
                <Tabs default-value="claude" class="mt-2 w-full">
                    <TabsList>
                        <TabsTrigger value="claude">
                            {{ $t("integrations.mcp.setup.claude_name") }}
                        </TabsTrigger>
                        <TabsTrigger value="chatgpt">
                            {{ $t("integrations.mcp.setup.chatgpt_name") }}
                        </TabsTrigger>
                    </TabsList>

                    <TabsContent value="claude">
                        <p class="text-sm text-iron-500">
                            {{ $t("integrations.mcp.setup.claude") }}
                        </p>
                    </TabsContent>
                    <TabsContent value="chatgpt">
                        <p class="text-sm text-iron-500">
                            {{ $t("integrations.mcp.setup.chatgpt") }}
                        </p>
                    </TabsContent>
                </Tabs>
            </div>

            <div v-if="mcp.connections.length > 0" class="mt-4">
                <p class="text-sm font-medium text-iron-700">
                    {{ $t("integrations.mcp.modal.connections") }}
                </p>
                <ul class="mt-2 gap-2 grid">
                    <li
                        v-for="connection in mcp.connections"
                        :key="connection.client_id"
                        class="rounded-lg border-iron-200 px-4 py-3 flex items-center justify-between border"
                    >
                        <div>
                            <p class="text-sm font-medium text-iron-700">
                                {{ connection.name }}
                            </p>
                            <p
                                v-if="connection.connected_at"
                                class="text-sm text-iron-500"
                            >
                                {{
                                    $t("integrations.mcp.modal.connected_at", {
                                        date: formatDate(
                                            connection.connected_at,
                                        ),
                                    })
                                }}
                            </p>
                        </div>
                        <Button
                            variant="destructive-ghost"
                            size="xs"
                            class="gap-1 flex"
                            @click="confirmRevoke(connection)"
                        >
                            <Trash :size="16" />
                            {{ $t("integrations.mcp.modal.revoke") }}
                        </Button>
                    </li>
                </ul>
            </div>
        </div>
    </IntegrationDetailDialog>
</template>

<script lang="ts" setup>
import { computed, ref } from "vue";
import IntegrationDetailDialog from "../IntegrationDetailDialog.vue";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Bot, Check, Copy, Trash } from "lucide-vue-next";
import { router, usePage } from "@inertiajs/vue3";
import route from "ziggy-js";
import useConfirm from "@/Pages/Zaptime/App/ConfirmDialog/composables/useConfirm";
import { useI18n } from "vue-i18n";
import type {
    McpConnection,
    McpIntegration,
} from "@/Pages/Zaptime/App/Integrations/types";

const page = usePage<{ mcp?: McpIntegration; locale?: string }>();
const mcp = computed(() => page.props.mcp);

withDefaults(
    defineProps<{
        showTrigger?: boolean;
    }>(),
    {
        showTrigger: true,
    },
);

const detailDialogOpen = defineModel<boolean>("open", { default: false });
const copied = ref(false);
const { show: showConfirm } = useConfirm();
const { t } = useI18n();

function formatDate(date: string) {
    return new Date(date).toLocaleDateString(page.props.locale ?? "en-US");
}

async function copyServerUrl() {
    if (!mcp.value) return;
    try {
        await navigator.clipboard.writeText(mcp.value.server_url);
    } catch {
        const textArea = document.createElement("textarea");
        textArea.value = mcp.value.server_url;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand("copy");
        document.body.removeChild(textArea);
    }
    copied.value = true;
    setTimeout(() => {
        copied.value = false;
    }, 2000);
}

async function confirmRevoke(connection: McpConnection) {
    const confirmed = await showConfirm({
        header: t("integrations.mcp.modal.revoke_title", {
            name: connection.name,
        }),
        message: t("integrations.mcp.modal.revoke_description"),
        confirm: t("integrations.mcp.modal.revoke"),
        cancel: t("cancel"),
    });

    if (confirmed) {
        router.delete(
            route("app.mcp-integrations.connections.delete", {
                clientId: connection.client_id,
            }),
            { preserveScroll: true },
        );
    }
}
</script>
